/**
 * Spin Wheels - Microgame Page
 *
 * A spinning wheel microgame where players wager FP and win or lose
 * based on where the wheel stops. Server determines outcome;
 * client only animates to the pre-determined result.
 *
 * Features:
 * - 3 emotionally distinct wheel types (Lucky Spin, Fortune Wheel, High Roller)
 * - Wager selector with presets
 * - Animation tiers: None / Reduced / Playful
 * - Free spins with multiplier → fixed fallback
 * - Result modal with outcome-specific copy
 */

import { logger } from '../lib/logger';
import { getAnimationPref, type AnimationTier } from '../lib/animation-pref';
import { showError } from '../lib/toast';
import { balanceService } from '../services/balance-service';
import { safeInitCelebrations } from '../utils/celebration-handler';
import { renderWheelSVG } from '../lib/spin-wheel-renderer';
import { runSequence, type SequenceHandle } from '../lib/animation-orchestrator';
import { fireAndForgetCelebration } from '../lib/animation-effects';
import type { WheelSegment, WheelConfig, WheelInfo } from '../lib/spin-wheel-renderer';
import { fireGuestEvent, fireGameCompleted, hookGuestCompletionToEOG } from '../lib/guest-conversion-engine';
import { isGuestUser } from '../lib/auth-state';
import { initAnimationsModal } from '../components/animation-tier-modal';
import { authModal, ensureAuthModalExists } from '../components/auth-modal';
import { showEndOfGameOverlay, buildSoloWinConfig, buildSoloBustConfig, type NextGameSuggestions } from '../components/end-of-game-overlay';
import { UserAPI } from '../utils/user-api';

// ---------------------------------------------------------------------------
// Types  (game-page-specific; shared types come from spin-wheel-renderer)
// ---------------------------------------------------------------------------

interface GameConfigResponse {
  enabled: boolean;
  guest_play: boolean;
  daily_limit: number;
  wheels: WheelInfo[];
}

interface SpinResponse {
  round_id: string;
  winning_segment_index: number;
  target_angle: number;
  segment_arc_angle: number;
  full_rotations_count: number;
  payout_type: string;
  payout_value: number;
  multiplier_applied: number | null;
  net_fp_change: number;
  new_balance: number;
  is_guest: boolean;
  is_free_spin: boolean;
  rewards: Array<{ amount: number; reason: string }>;
  achievements: string[];
  play_event_id?: string;
  next_game_suggestions?: NextGameSuggestions;
}

interface HistoryRound {
  id: string;
  difficulty_id: string;
  status: string;
  wager: number;
  net_fp_change: number;
  payout_type: string;
  winning_segment_index: number | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = '/api/games/spin-wheels';

// Wheel descriptions from plan UI copy
const WHEEL_DESCRIPTIONS: Record<string, string> = {
  lucky_spin: 'Steady rewards, lower risk.',
  fortune_wheel: 'Balanced risk and reward.',
  high_roller: 'Big wins. Bigger risks.',
};

const WHEEL_ICONS: Record<string, string> = {
  lucky_spin: 'bi-star',
  fortune_wheel: 'bi-bullseye',
  high_roller: 'bi-lightning',
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let gameConfig: GameConfigResponse | null = null;
let selectedWheel: WheelInfo | null = null;
let isSpinning = false;
let currentBalance = 0;
let isGuest = false;
let pendingEntryContext: string | undefined;
let eogHandle: SequenceHandle | undefined;

// ---------------------------------------------------------------------------
// DOM Helpers
// ---------------------------------------------------------------------------

function $(selector: string): HTMLElement | null {
  return document.querySelector(selector);
}

function show(el: HTMLElement | null): void {
  if (el) el.style.display = '';
}

function hide(el: HTMLElement | null): void {
  if (el) el.style.display = 'none';
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

async function fetchConfig(): Promise<GameConfigResponse> {
  const response = await fetch(`${API_BASE}/config`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to load config' }));
    throw new Error(err.error || 'Failed to load config');
  }
  return response.json();
}

async function doSpin(wheelId: string, wager: number, entryContext?: string): Promise<SpinResponse> {
  const response = await fetch(`${API_BASE}/spin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wheel_id: wheelId, wager, ...(entryContext ? { entry_context: entryContext } : {}) }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Spin failed' }));
    const error = new Error(err.error || 'Spin failed');
    (error as Error & { status: number }).status = response.status;
    throw error;
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------

function getAnimationTier(): 'none' | 'reduced' | 'playful' {
  return getAnimationPref();
}

function getAnimationDuration(): number {
  const tier = getAnimationTier();
  switch (tier) {
    case 'none': return 300;
    case 'reduced': return 1500;
    case 'playful': return 4000;
  }
}

function applyAnimationTierClass(): void {
  const page = $('#spinPage');
  if (!page) return;
  page.classList.remove('spin-anim-none', 'spin-anim-reduced', 'spin-anim-playful');
  page.classList.add(`spin-anim-${getAnimationTier()}`);
}

function initSpinAnimationsModal(): void {
  initAnimationsModal({
    gamePrefix: 'spin',
    cssClassPrefix: 'spin-anim',
    pageElementId: 'spinPage',
  });
}

// ---------------------------------------------------------------------------
// Wheel Rendering  (delegates to shared spin-wheel-renderer)
// ---------------------------------------------------------------------------

function renderWheel(wheel: WheelInfo): void {
  const wheelEl = $('#spinWheel');
  if (!wheelEl) return;
  renderWheelSVG(wheelEl, wheel.wheel_config.segments);
}

function renderWheelCards(wheels: WheelInfo[]): void {
  const container = $('#wheelCards');
  if (!container) return;

  container.innerHTML = wheels.map(wheel => {
    const desc = WHEEL_DESCRIPTIONS[wheel.id] || '';
    const icon = WHEEL_ICONS[wheel.id] || 'bi-bullseye';
    const freeSpins = wheel.free_spins_remaining;
    const isHighRoller = (wheel.wheel_config.max_loss_multiplier || 1) > 1;

    return `
      <div class="col-md-4 col-sm-6">
        <div class="card gc-card h-100 text-center" data-wheel-id="${wheel.id}" data-testid="wheel-card-${wheel.id}">
          <div class="card-body py-4">
          <div class="gc-card-icon" style="color: var(--bs-primary);"><i class="bi ${icon}"></i></div>
          <div class="gc-card-label">${wheel.label}</div>
          <div class="gc-card-tagline">${desc}</div>
          ${freeSpins > 0 ? `<span class="badge bg-success mb-1"><i class="bi bi-gift me-1"></i>${freeSpins} free spin${freeSpins > 1 ? 's' : ''}</span>` : ''}
          ${isHighRoller ? '<span class="badge bg-warning text-dark"><i class="bi bi-exclamation-triangle me-1"></i>High Risk</span>' : ''}
          <div class="mt-auto pt-2">
            <small class="text-muted">Max wager: ${wheel.wheel_config.max_wager} FP</small>
          </div>
          </div>
        </div>
      </div>
    `;
  }).join('');

  // Attach click handlers
  container.querySelectorAll('.gc-card').forEach(card => {
    card.addEventListener('click', () => {
      const wheelId = (card as HTMLElement).dataset.wheelId;
      const wheel = gameConfig?.wheels.find(w => w.id === wheelId);
      if (wheel) selectWheel(wheel);
    });
  });
}

// ---------------------------------------------------------------------------
// Wheel Selection & Wager
// ---------------------------------------------------------------------------

function selectWheel(wheel: WheelInfo): void {
  selectedWheel = wheel;
  logger.info('[SpinWheels] Selected wheel:', wheel.id);

  // Save wheel preference (authenticated users only)
  if (!isGuest) {
    UserAPI.setPreference('spin_difficulty', wheel.id).catch(() => {});
  }

  hide($('#wheelSelection'));
  show($('#gameArea'));
  show($('#backToSelection'));

  const iconEl = $('#selectedWheelIcon');
  if (iconEl) {
    const iconClass = WHEEL_ICONS[wheel.id] || 'bi-bullseye';
    iconEl.innerHTML = `<i class="bi ${iconClass}" style="font-size:1.4rem;"></i> <span class="small">${wheel.label}</span>`;
  }

  renderWheel(wheel);
  updateWagerUI();
  applyAnimationTierClass();

  // High Roller warning
  const warningEl = $('#highRollerWarning');
  if (warningEl) {
    if ((wheel.wheel_config.max_loss_multiplier || 1) > 1) {
      show(warningEl);
    } else {
      hide(warningEl);
    }
  }
}

function updateWagerUI(): void {
  if (!selectedWheel) return;

  const maxWagerEl = $('#maxWagerDisplay');
  const balanceEl = $('#balanceDisplay');
  const freeSpinInfo = $('#freeSpinInfo');
  const freeSpinText = $('#freeSpinText');
  const wagerSlider = document.getElementById('wagerSlider') as HTMLInputElement | null;
  const wagerDisplay = $('#wagerAmountDisplay');

  const maxWager = selectedWheel.wheel_config.max_wager;
  const effectiveMax = Math.min(maxWager, currentBalance);

  if (maxWagerEl) maxWagerEl.textContent = String(maxWager);
  if (balanceEl) balanceEl.textContent = String(currentBalance);

  // Sync slider
  if (wagerSlider) {
    wagerSlider.max = String(effectiveMax);
    // Clamp current value
    const current = parseInt(wagerSlider.value, 10) || 0;
    if (current > effectiveMax) {
      wagerSlider.value = String(effectiveMax);
      setWagerValue(effectiveMax);
    }
  }

  // Sync display text
  if (wagerDisplay) {
    const val = getWagerValue();
    wagerDisplay.innerHTML = `<span class="text-muted small">Your wager:</span> ${val} FP`;
  }

  // Free spin info
  const freeRemaining = selectedWheel.free_spins_remaining;
  if (freeSpinInfo && freeSpinText) {
    if (freeRemaining > 0) {
      show(freeSpinInfo);
      freeSpinText.textContent = `${freeRemaining} free spin${freeRemaining > 1 ? 's' : ''} remaining today`;
    } else {
      hide(freeSpinInfo);
    }
  }

  // Show/hide preset buttons based on max wager and free spins
  document.querySelectorAll<HTMLElement>('.wager-preset').forEach(btn => {
    const amount = btn.dataset.amount;
    if (!amount) return;
    if (amount === '0') {
      // "Free" preset — only show if free spins remain
      btn.style.display = freeRemaining > 0 ? '' : 'none';
    } else if (amount === 'max') {
      // "Max" always visible
      btn.style.display = '';
    } else {
      // Numeric presets — hide if value exceeds max wager or balance
      const val = parseInt(amount, 10);
      btn.style.display = val <= effectiveMax ? '' : 'none';
    }
  });
}

function getWagerValue(): number {
  const input = document.getElementById('wagerInput') as HTMLInputElement | null;
  return input ? Math.max(0, parseInt(input.value, 10) || 0) : 0;
}

function setWagerValue(val: number): void {
  const input = document.getElementById('wagerInput') as HTMLInputElement | null;
  if (input) input.value = String(Math.max(0, val));
}

// ---------------------------------------------------------------------------
// Spin Logic
// ---------------------------------------------------------------------------

async function handleSpin(): Promise<void> {
  if (isSpinning || !selectedWheel) return;

  // Cancel any pending end-of-game sequence from a previous spin
  eogHandle?.cancel();
  eogHandle = undefined;

  const wager = getWagerValue();

  // Client-side validation
  if (wager > 0 && wager > currentBalance) {
    showError('Not enough FP for this wager');
    return;
  }

  if (wager > selectedWheel.wheel_config.max_wager) {
    showError(`Maximum wager is ${selectedWheel.wheel_config.max_wager} FP`);
    return;
  }

  isSpinning = true;
  window.__versionGuard?.setSafeToReload(false);
  updateSpinButton('spinning');

  // Consume pending entry context (one-shot)
  const ec = pendingEntryContext;
  pendingEntryContext = undefined;

  try {
    const result = await doSpin(selectedWheel.id, wager, ec);
    logger.info('[SpinWheels] Spin result:', result);

    // Animate wheel
    await animateWheel(result);

    // Update balance
    if (!result.is_guest) {
      currentBalance = result.new_balance;
      // Refresh navbar FP display
      balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
    }
    updateWagerUI();

    // Update free spins count
    if (result.is_free_spin && selectedWheel) {
      selectedWheel.free_spins_remaining = Math.max(0, selectedWheel.free_spins_remaining - 1);
      updateWagerUI();
    }

    // Show result
    showResultModal(result, wager);
    void loadStats();

  } catch (error: unknown) {
    const errMsg = error instanceof Error ? error.message : 'Spin failed';
    logger.error('[SpinWheels] Spin error:', errMsg);

    // No free spins → show modal (friendly) instead of a toast
    if (errMsg.toLowerCase().includes('no free spins')) {
      if (isGuest) {
        showGuestNoFreeSpinsModal();
      } else {
        showAuthNoFreeSpinsModal();
      }
    } else {
      showError(errMsg);
    }
  } finally {
    isSpinning = false;
    window.__versionGuard?.setSafeToReload(true);
    updateSpinButton('default');
  }
}

function updateSpinButton(state: 'default' | 'spinning' | 'disabled'): void {
  const btn = $('#spinBtn') as HTMLButtonElement | null;
  if (!btn) return;

  switch (state) {
    case 'default':
      btn.disabled = false;
      btn.innerHTML = '<i class="bi bi-arrow-repeat me-2"></i>Spin the Wheel';
      break;
    case 'spinning':
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status"></span>Spinning...';
      break;
    case 'disabled':
      btn.disabled = true;
      btn.innerHTML = 'Not enough FP';
      break;
  }
}

async function animateWheel(result: SpinResponse): Promise<void> {
  const wheelEl = $('#spinWheel');
  const pointerEl = document.querySelector('.spin-wheel-pointer') as HTMLElement | null;
  if (!wheelEl) return;

  const fullRotations = result.full_rotations_count * 360;
  const totalAngle = fullRotations + result.target_angle;
  const duration = getAnimationDuration();
  const tier = getAnimationTier();

  // Reset transform without transition
  wheelEl.style.transition = 'none';
  wheelEl.style.transform = 'rotate(0deg)';

  // Force reflow
  void wheelEl.offsetHeight;

  // Start pointer wobble for playful / reduced tiers
  if (pointerEl && tier !== 'none') {
    pointerEl.classList.add('wobbling');
  }

  // Apply spin animation
  switch (tier) {
    case 'none':
      wheelEl.style.transition = `transform ${duration}ms ease-out`;
      break;
    case 'reduced':
      wheelEl.style.transition = `transform ${duration}ms cubic-bezier(0.25, 0.1, 0.25, 1)`;
      break;
    case 'playful':
      wheelEl.style.transition = `transform ${duration}ms cubic-bezier(0.15, 0.60, 0.08, 1.00)`;
      wheelEl.classList.add('spinning');
      break;
  }

  wheelEl.style.transform = `rotate(${totalAngle}deg)`;

  // Wait for animation to complete
  await new Promise(resolve => setTimeout(resolve, duration + 100));

  wheelEl.classList.remove('spinning');
  if (pointerEl) pointerEl.classList.remove('wobbling');
}

// ---------------------------------------------------------------------------
// Result Modal
// ---------------------------------------------------------------------------

function showResultModal(result: SpinResponse, wager: number): void {
  const net = result.net_fp_change;
  const isFree = result.is_free_spin;
  const ngs = result.next_game_suggestions;

  // Determine outcome-specific messaging
  let message: string;
  let subtitle: string | undefined;
  const isLoss = net < 0;

  if (isFree) {
    message = 'Free Spin Result';
    subtitle = net > 0 ? `+${net} FP` : `${net} FP`;
  } else if (net > 0) {
    const isJackpot = net >= wager * 5 && wager > 0;
    message = isJackpot ? 'JACKPOT! 🎰' : 'Nice spin! 🎉';
    subtitle = `+${net} FP`;
  } else if (net < 0) {
    const isHighRollerLoss = selectedWheel && (selectedWheel.wheel_config.max_loss_multiplier || 1) > 1 && Math.abs(net) > wager;
    message = isHighRollerLoss ? 'High Roller bites hard.' : 'Tough luck.';
    subtitle = `${net} FP`;
  } else {
    message = 'Nothing this time.';
    subtitle = 'Spin again!';
  }

  const onSpinAgain = (): void => {
    void fireGameCompleted(isGuest);
    // User stays on the page — overlay close is sufficient
  };

  const config = isLoss
    ? buildSoloBustConfig({
        game: 'spin-wheels',
        fpPenalty: Math.abs(net),
        isGuest,
        onPlayAgain: onSpinAgain,
        playAgainUrl: '/games/spin-wheels',
        playEventId: result.play_event_id,
        nextGameSuggestions: ngs,
      })
    : buildSoloWinConfig({
        game: 'spin-wheels',
        totalScore: net,
        fpEarned: !isGuest && net > 0 ? net : undefined,
        isGuest,
        onPlayAgain: onSpinAgain,
        playAgainUrl: '/games/spin-wheels',
        playEventId: result.play_event_id,
        nextGameSuggestions: ngs,
      });

  // Override with spin-specific messaging
  config.message = message;
  if (subtitle) config.subtitle = subtitle;
  config.icon = 'bi-bullseye';

  // Override primary label for spin context (only when no auto-next)
  if (!isGuest && !ngs?.auto_next) {
    config.primaryAction.label = 'Spin Again!';
    config.primaryAction.icon = 'bi-arrow-clockwise';
  }

  // Determine celebration step
  const steps = [];
  if (result.net_fp_change >= 20) {
    steps.push(fireAndForgetCelebration('big_win'));
  } else if (result.multiplier_applied && result.multiplier_applied >= 3) {
    steps.push(fireAndForgetCelebration('reward'));
  }

  // Orchestrated: optional celebration → overlay
  eogHandle?.cancel();
  eogHandle = runSequence('spin-wheels-eog', steps, () => {
    showEndOfGameOverlay(config);
    hookGuestCompletionToEOG(isGuest);
  });
}

// ---------------------------------------------------------------------------
// Guest No-Free-Spins Modal
// ---------------------------------------------------------------------------

function showGuestNoFreeSpinsModal(): void {
  // Remove any previous instance
  const existingModal = document.getElementById('guestNoFreeSpinsModal');
  if (existingModal) existingModal.remove();

  const modalEl = document.createElement('div');
  modalEl.id = 'guestNoFreeSpinsModal';
  modalEl.className = 'modal fade';
  modalEl.tabIndex = -1;
  modalEl.setAttribute('aria-hidden', 'true');
  modalEl.innerHTML = `
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header border-0">
          <h5 class="modal-title">No Free Spins</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-center py-4">
          <p class="fs-1 mb-2">🎰</p>
          <p class="fw-semibold mb-2">No free spins as guest remaining today.</p>
          <p class="text-muted small mb-2">
            <i class="bi bi-info-circle me-1"></i>Sign in to earn Fortunio Points!<br>
            Earn FP by playing games and unlocking achievements.
          </p>
        </div>
        <div class="modal-footer border-0 justify-content-center">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Maybe later</button>
          <a href="/auth/login" class="btn btn-primary">
            <i class="bi bi-person-plus me-1"></i>Sign-in to start earning points
          </a>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modalEl);

  const modal = new bootstrap.Modal(modalEl);
  modal.show();

  // Clean up DOM after hide
  modalEl.addEventListener('hidden.bs.modal', () => {
    modalEl.remove();
  }, { once: true });
}

// ---------------------------------------------------------------------------
// No-Free-Spins Modal — Authenticated User
// ---------------------------------------------------------------------------

function showAuthNoFreeSpinsModal(): void {
  const existingModal = document.getElementById('authNoFreeSpinsModal');
  if (existingModal) existingModal.remove();

  const modalEl = document.createElement('div');
  modalEl.id = 'authNoFreeSpinsModal';
  modalEl.className = 'modal fade';
  modalEl.tabIndex = -1;
  modalEl.setAttribute('aria-hidden', 'true');
  modalEl.innerHTML = `
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header border-0">
          <h5 class="modal-title">No Free Spins Left Today</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-center py-4">
          <p class="fs-1 mb-2">🎰</p>
          <p class="fw-semibold mb-2">You've used all your free spins for today.</p>
          <p class="text-muted small mb-0">Come back tomorrow — or wager some FP to keep spinning!</p>
        </div>
        <div class="modal-footer border-0 justify-content-center">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Got it</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modalEl);

  const modal = new bootstrap.Modal(modalEl);
  modal.show();

  modalEl.addEventListener('hidden.bs.modal', () => {
    modalEl.remove();
  }, { once: true });
}

// ---------------------------------------------------------------------------
// Balance
// ---------------------------------------------------------------------------

async function loadBalance(): Promise<void> {
  try {
    const resp = await fetch('/api/user/me', { credentials: 'same-origin' });
    if (resp.ok) {
      const data = await resp.json();
      currentBalance = data.fp_balance ?? 0;
    } else {
      currentBalance = 0;
    }
  } catch {
    currentBalance = 0;
  }
}

async function loadGuestStatus(): Promise<void> {
  try {
    const resp = await fetch('/me', { credentials: 'same-origin' });
    if (resp.ok) {
      const data = await resp.json();
      isGuest = isGuestUser(data);
    } else {
      isGuest = true;
    }
  } catch {
    isGuest = true;
  }
  logger.info(`[SpinWheels] Guest status: ${isGuest}`);
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

async function init(): Promise<void> {
  logger.info('[SpinWheels] Initializing...');
  const loadingEl = $('#loadingState');
  const selectionEl = $('#wheelSelection');

  show(loadingEl);
  hide(selectionEl);
  hide($('#gameArea'));

  try {
    // Load config, balance, and guest status in parallel
    const [config] = await Promise.all([
      fetchConfig(),
      loadBalance(),
      loadGuestStatus(),
    ]);

    gameConfig = config;
    logger.info('[SpinWheels] Config loaded:', config.wheels.length, 'wheels');

    if (!config.wheels.length) {
      showError('No wheels available right now');
      return;
    }

    renderWheelCards(config.wheels);
    hide(loadingEl);
    show(selectionEl);
    show($('#gameControls'));

  } catch (error: unknown) {
    logger.error('[SpinWheels] Init failed:', error);
    hide(loadingEl);
    showError('Could not load game. Please try again later.');
  }

  // Wire up event listeners
  setupEventListeners();

  // Initialize Bootstrap tooltips (navbar info icon)
  const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
  tooltipTriggerList.forEach(el => new bootstrap.Tooltip(el as HTMLElement));

  initSpinAnimationsModal();
  safeInitCelebrations();
  void loadStats();

  // Read URL params for guided play flow
  const urlParams = new URLSearchParams(window.location.search);
  const ecParam = urlParams.get('ec');
  const difficultyParam = urlParams.get('difficulty');
  if (ecParam) {
    pendingEntryContext = ecParam;
  }
  if (urlParams.has('ec') || urlParams.has('difficulty')) {
    history.replaceState({}, '', window.location.pathname);
  }

  // Auto-select wheel if difficulty param matches a wheel id
  if (difficultyParam && difficultyParam !== 'auto' && gameConfig) {
    const matchingWheel = gameConfig.wheels.find(w => w.id === difficultyParam);
    if (matchingWheel) {
      selectWheel(matchingWheel);
    }
  } else if (difficultyParam === 'auto' && gameConfig) {
    // 'auto' → try saved preference, fall back to wheel selection screen
    const saved = await UserAPI.getPreference('spin_difficulty').catch(() => null);
    if (typeof saved === 'string' && saved) {
      const matchingWheel = gameConfig.wheels.find(w => w.id === saved);
      if (matchingWheel) {
        selectWheel(matchingWheel);
      }
    }
  }
  // No match / no pref → show wheel selection screen
}

async function loadStats(): Promise<void> {
  // Show guest placeholder if not authenticated
  if (isGuest) {
    const statsCard = document.getElementById('statsCard');
    if (statsCard) {
      const cardBody = statsCard.querySelector('.card-body');
      if (cardBody) {
        cardBody.innerHTML = `
          <h6 class="text-muted mb-3">Your Stats</h6>
          <div class="text-center py-3" data-testid="guest-stats-placeholder">
            <p class="mb-1 text-muted">Your game stats will appear here</p>
            <p class="small text-muted mb-2">Available when signed in</p>
            <a href="#" class="btn btn-outline-primary btn-sm" data-testid="guest-stats-signin">
              Sign in to start tracking →
            </a>
          </div>`;
        const signinLink = cardBody.querySelector('[data-testid="guest-stats-signin"]');
        if (signinLink) {
          signinLink.addEventListener('click', (e) => {
            e.preventDefault();
            void fireGuestEvent('tried_locked_feature', true).then((shown) => {
              if (!shown) {
                ensureAuthModalExists();
                authModal.show();
              }
            });
          });
        }
      }
    }
    return;
  }

  try {
    const resp = await fetch('/api/games/spin-wheels/stats');
    if (!resp.ok) return;
    const data = await resp.json() as { total_spins: number; wins: number; best_win: number };
    const spins = document.getElementById('statSpins');
    const wins = document.getElementById('statWins');
    const bestWin = document.getElementById('statBestWin');
    if (spins) spins.textContent = String(data.total_spins);
    if (wins) wins.textContent = String(data.wins);
    if (bestWin) bestWin.textContent = `${data.best_win} FP`;
  } catch (err) {
    logger.warn('[SpinWheels] Failed to load stats', err);
  }
}

function setupEventListeners(): void {
  // Spin button
  const spinBtn = $('#spinBtn');
  if (spinBtn) spinBtn.addEventListener('click', handleSpin);

  // Back to selection
  const backBtn = $('#backToSelection');
  if (backBtn) {
    backBtn.addEventListener('click', () => {
      // Cancel any pending end-of-game sequence
      eogHandle?.cancel();
      eogHandle = undefined;

      selectedWheel = null;
      hide($('#gameArea'));
      hide($('#backToSelection'));
      show($('#wheelSelection'));
      // Refresh config so free spin counts are up-to-date
      refreshConfig();
    });
  }

  // Wager slider → syncs hidden input & display
  const wagerSlider = document.getElementById('wagerSlider') as HTMLInputElement | null;
  const wagerDisplay = $('#wagerAmountDisplay');

  if (wagerSlider) {
    wagerSlider.addEventListener('input', () => {
      const val = parseInt(wagerSlider.value, 10) || 0;
      setWagerValue(val);
      if (wagerDisplay) wagerDisplay.innerHTML = `<span class="text-muted small">Your wager:</span> ${val} FP`;
    });
  }

  // Wager presets
  document.querySelectorAll('.wager-preset').forEach(btn => {
    btn.addEventListener('click', () => {
      const amount = (btn as HTMLElement).dataset.amount;
      if (!amount || !selectedWheel) return;
      const effectiveMax = Math.min(selectedWheel.wheel_config.max_wager, currentBalance);
      let val: number;
      if (amount === 'max') {
        val = effectiveMax;
      } else {
        val = Math.min(parseInt(amount, 10), effectiveMax);
      }
      setWagerValue(val);
      if (wagerSlider) wagerSlider.value = String(val);
      if (wagerDisplay) wagerDisplay.innerHTML = `<span class="text-muted small">Your wager:</span> ${val} FP`;
    });
  });
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', init);

// Re-fetch config when returning via back/forward navigation (bfcache)
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    logger.info('[SpinWheels] Page restored from bfcache — refreshing config');
    refreshConfig();
  }
});

/**
 * Re-fetch wheel config and update the card display (free spins, etc.)
 * without resetting the full page state.
 */
async function refreshConfig(): Promise<void> {
  try {
    const config = await fetchConfig();
    gameConfig = config;

    // Re-render selection cards with fresh free spin counts
    if (config.wheels.length) {
      renderWheelCards(config.wheels);
    }

    // If a wheel is currently selected, update it with fresh data
    if (selectedWheel) {
      const fresh = config.wheels.find(w => w.id === selectedWheel!.id);
      if (fresh) {
        selectedWheel = fresh;
        selectWheel(fresh);
      }
    }

    // Refresh balance too
    await loadBalance();
  } catch (err) {
    logger.error('[SpinWheels] Failed to refresh config:', err);
  }
}
